<script setup lang="ts">
import type { HTMLAttributes } from "vue";
import { useVModel } from "@vueuse/core";
import { cn } from "@/lib/utils";
import { ref, onMounted, watch } from "vue";
type Option = "minute" | "hour" | "day" | "week";

const props = withDefaults(
    defineProps<{
        defaultValue?: number;
        modelValue: number | null;
        class?: HTMLAttributes["class"];
        options?: Option[];
        hideIntervals?: string[];
    }>(),
    {
        options: () => ["hour", "day", "week"],
    },
);

const emits = defineEmits<{
    (e: "update:modelValue", payload: number | undefined): void;
}>();

const modelValue = useVModel(props, "modelValue", emits, {
    passive: true,
    defaultValue: props.defaultValue,
});

type TimeFormat = {
    value: number;
    unit: "second" | "minute" | "hour" | "day" | "week";
};

const selectedUnit = ref<TimeFormat["unit"]>("hour");
const calculatedValue = ref<number>();

function formatTime(seconds: number): TimeFormat {
    const minute = 60;
    const hour = 60 * minute;
    const day = 24 * hour;
    const week = 7 * day;

    if (seconds % week === 0 && seconds >= week) {
        return { value: seconds / week, unit: "week" };
    } else if (seconds % day === 0 && seconds >= day) {
        return { value: seconds / day, unit: "day" };
    } else if (seconds % hour === 0 && seconds >= hour) {
        return { value: seconds / hour, unit: "hour" };
    } else if (seconds % minute === 0 && seconds >= minute) {
        return { value: seconds / minute, unit: "minute" };
    } else {
        return { value: seconds, unit: "second" };
    }
}

function toSeconds(time: TimeFormat): number {
    const { value, unit } = time;

    switch (unit) {
        case "second":
            return value;
        case "minute":
            return value * 60;
        case "hour":
            return value * 60 * 60;
        case "day":
            return value * 24 * 60 * 60;
        case "week":
            return value * 7 * 24 * 60 * 60;
        default:
            throw new Error(`Unsupported unit: ${unit}`);
    }
}

function onValueChange(e: Event) {
    if (selectedUnit.value === undefined) {
        return;
    }

    calculatedValue.value = parseInt((e.target as HTMLInputElement).value);

    if (calculatedValue.value < 0) {
        calculatedValue.value = 0;
    }

    if (isNaN(calculatedValue.value)) {
        calculatedValue.value = undefined;
        emits("update:modelValue", undefined);
        return;
    }

    emits(
        "update:modelValue",
        toSeconds({ value: calculatedValue.value, unit: selectedUnit.value }),
    );
}

function onUnitChange() {
    if (
        calculatedValue.value === undefined ||
        selectedUnit.value === undefined
    ) {
        return;
    }

    emits(
        "update:modelValue",
        toSeconds({ value: calculatedValue.value, unit: selectedUnit.value }),
    );
}

watch(selectedUnit, onUnitChange);

onMounted(() => {
    if (modelValue.value === null) {
        return;
    }
    const { value, unit } = formatTime(modelValue.value);
    selectedUnit.value = unit;
    calculatedValue.value = value;
});
</script>

<template>
    <div class="flex">
        <input
            :value="calculatedValue"
            @input="onValueChange"
            type="number"
            min="0"
            :class="
                cn(
                    'dark:bg-iron-950 dark:ring-offset-iron-950 h-10 rounded-l-lg border-iron-300 bg-white px-3 py-2 text-sm shadow-xs ring-offset-white file:text-sm file:font-medium placeholder:text-iron-500 focus-visible:border-z-primary-300 focus-visible:ring-z-primary-100 dark:border-iron-800 dark:placeholder:text-iron-400 dark:focus-visible:ring-iron-300 flex w-full border file:border-0 file:bg-transparent focus-visible:ring-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
                    props.class ?? '',
                )
            "
        />
        <select
            v-model="selectedUnit"
            class="h-10 rounded-r-lg border-iron-300 text-sm text-iron-600 focus-visible:border-z-primary-300 focus-visible:ring-z-primary-100 min-w-[100px] border-l-0 focus:outline-none focus-visible:ring-2 focus-visible:outline-none"
        >
            <option v-if="!hideIntervals?.includes('minutes')" value="minute">
                {{ $t("time_select.minute", calculatedValue) }}
            </option>
            <option value="hour">
                {{ $t("time_select.hour", calculatedValue) }}
            </option>
            <option value="day">
                {{ $t("time_select.day", calculatedValue) }}
            </option>
            <option v-if="!hideIntervals?.includes('weeks')" value="week">
                {{ $t("time_select.week", calculatedValue) }}
            </option>
        </select>
    </div>
</template>
